import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { parseJwt } from '@common/utils';
import { usePostHog } from 'posthog-js/react';
import useAuthStore from '@/store/AuthStore';
import { ECookieKey, OrganizationRoles } from '@/enums';
import { useGetOrganizationsOnLogin, useGetUserWithToken } from '@/reactQuery/get';
import useUserStore from '@/store/UserStore';
import Routes from '@/router/routes';
import { AuthData, Organization } from '@/types';

export interface UseAuthenticationOptions {
  /**
   * When true, the hook will NOT navigate away when an auth token is already
   * present, and `processAuthSuccess` will NOT navigate internally after
   * setting up auth. Callers that need to chain a specific navigation after
   * login (e.g. the accept-invitation page) pass true so they can control
   * the final destination themselves.
   */
  skipAutoRedirect?: boolean;
}

export function useAuthentication(options: UseAuthenticationOptions = {}) {
  const { skipAutoRedirect = false } = options;
  const navigate = useNavigate();
  const location = useLocation();
  const [redirectTo, setRedirectTo] = useState('/');
  const [error, setError] = useState<string>('');
  const sessionExpiredRedirectionLink = localStorage.getItem('sessionExpiredRedirectTo');
  const posthog = usePostHog();

  const from = location.state?.from || redirectTo;

  const { authToken, setAuthToken, setRequireTwoFactor } = useAuthStore();
  const { setUser } = useUserStore();
  const { mutateAsync: fetchOrganizations } = useGetOrganizationsOnLogin();
  const { mutateAsync: getUserWithToken } = useGetUserWithToken();

  /**
   * TELV1-278: reported by Slobodan — login flickered on production because
   * the auto-redirect useEffect below fired the moment `setAuthToken` ran,
   * BEFORE `setLoginCookies` had refreshed MAIN_ORGANIZATION and
   * FALLBACK_WORKSPACE. App.tsx then redirected using stale cookies from
   * the previous login session, briefly painted the wrong workspace, then
   * processAuthSuccess finished and navigated again to the correct one.
   *
   * Two coordinated fixes:
   *   1. setAuthToken is now called AFTER setLoginCookies (see below).
   *   2. This ref suppresses the auto-redirect useEffect once
   *      processAuthSuccess has already navigated, so it doesn't bounce
   *      the user back to `/` after we've already landed on the resolved
   *      workspace URL.
   *
   * The "user lands on /login while already authed" entry case still works
   * (ref stays false on mount; useEffect navigates as before).
   */
  const justNavigatedFromProcessAuth = useRef(false);

  const getValidOrganization = (organizations: Organization[], userId: string): Organization => {
    const workspace = organizations.find((organization: Organization) =>
      organization.members.some(
        ({ user_id, role }) => user_id === userId && role === OrganizationRoles.OWNER,
      ),
    );

    if (workspace && !workspace.isHidden) {
      return workspace;
    }

    return organizations.find(
      (userWorkspace) =>
        !userWorkspace.isHidden && userWorkspace.members.some(({ user_id }) => user_id === userId),
    );
  };

  // Set login cookies
  const setLoginCookies = (data: AuthData, remember: boolean = true) => {
    // TELV1-296 follow-up — explicit `secure` + `sameSite` options.
    // Previously `Cookies.set(key, value, { expires })` relied entirely
    // on browser defaults. On production (https) modern browsers emit a
    // console warning when SameSite is implicit, and in some
    // cross-context navigations the cookie would not be sent. Setting
    // them explicitly:
    //   - `secure: true` in production builds (cookie only sent over https;
    //     suppressed in dev so localhost http still works)
    //   - `sameSite: 'lax'` (the modern browser default but stated
    //     explicitly so future browser changes don't shift behaviour)
    // The `expires` ⊆ remember-me window (14d remembered, 1d transient)
    // is unchanged.
    const cookieOptions = {
      expires: remember ? 14 : 1,
      secure: import.meta.env.PROD,
      sameSite: 'lax' as const,
    };
    const cookiesToSet = {
      [ECookieKey.TOKEN]: data?.token,
      [ECookieKey.FALLBACK_WORKSPACE]: data?.mainWorkspace,
      [ECookieKey.MAIN_ORGANIZATION]: data?._id,
      [ECookieKey.REMEMBER_ME]: remember.toString(),
    };

    Object.entries(cookiesToSet).forEach(([key, value]) => {
      if (value) {
        Cookies.set(key, value, cookieOptions);
      }
    });
  };

  // Process authentication success and handle navigation.
  //
  // Returns an object describing outcome so callers can chain navigation
  // safely. When `skipAutoRedirect` is true (passed to the hook), this
  // function will NOT navigate internally — the caller is responsible for
  // navigating after auth state is set up. This prevents the previous race
  // where the accept-invitation page would call processAuthSuccess (which
  // navigated to "/") and then try to navigate to the target workspace,
  // landing the user on the dashboard root instead of their invited
  // workspace.
  const processAuthSuccess = async (
    token: string,
    remember: boolean = true,
  ): Promise<{ success: boolean; currentOrganization?: string; currentWorkspace?: string }> => {
    try {
      if (!token) {
        setError('Authentication failed. Please try again.');
        return { success: false };
      }

      // TELV1-278: do NOT call setAuthToken yet — it would trigger the
      // auto-redirect useEffect below, which would read STALE
      // MAIN_ORGANIZATION / FALLBACK_WORKSPACE cookies (still set from a
      // previous login session) and bounce the user through the wrong
      // workspace. We set authToken further down, AFTER setLoginCookies
      // has refreshed those cookies.

      // Parse token to get user ID
      const parsedToken = parseJwt(token);
      if (!parsedToken || !parsedToken.userId) {
        setError('Authentication failed. Invalid credentials.');
        return { success: false };
      }

      // Fetch organizations and user data in parallel
      const [{ data: organizations }, userData] = await Promise.all([
        fetchOrganizations(token),
        getUserWithToken(token),
      ]);

      // Store user data in Zustand to avoid duplicate API calls
      if (userData) {
        setUser(userData);
      }

      // Determine current organization and workspace for PostHog identification
      const last_organization = userData?.last_organization;
      const last_workspace = userData?.last_workspace;

      // Get valid workspace
      const validOrganization = getValidOrganization(organizations, parsedToken.userId);
      if (!validOrganization) {
        setError('No valid organization found. Please contact support.');
        return { success: false };
      }

      const { _id, mainWorkspace } = validOrganization || {};

      // Make sure we have values for organization and workspace
      if (!_id) {
        setError('Failed to get organization details. Please contact support.');
        return { success: false };
      }

      if (!mainWorkspace) {
        setError('Failed to get workspace details. Please contact support.');
        return { success: false };
      }

      // Validate last_organization is not hidden before using it
      const lastOrgIsValid =
        last_organization &&
        organizations.some((org) => org._id === last_organization && !org.isHidden);

      const currentOrganization = lastOrgIsValid ? last_organization : _id;
      const currentWorkspace = lastOrgIsValid ? last_workspace || mainWorkspace : mainWorkspace;

      // Identify user with PostHog including organization and workspace context
      if (userData && posthog) {
        try {
          posthog.identify(userData._id, {
            email: userData.email,
            organization_id: currentOrganization,
            workspace_id: currentWorkspace,
            user_role: userData.role,
          });
        } catch {
          // Analytics failure should not break the application
        }
      }

      // Set cookies FIRST so the auto-redirect useEffect (and any
      // downstream App.tsx redirect) reads fresh values, not stale ones
      // from a previous login session. See TELV1-278 flicker fix.
      setLoginCookies(
        {
          token,
          _id: currentOrganization,
          mainWorkspace: currentWorkspace,
        },
        remember,
      );

      // Set the redirect path now so any caller reading `redirectTo` from
      // the hook sees the resolved workspace URL.
      const redirectPath = `${Routes.organization.path}/${currentOrganization}${Routes.workspace.path}/${currentWorkspace}`;
      setRedirectTo(redirectPath);

      if (sessionExpiredRedirectionLink) {
        // If session expired redirection page is set, navigate to that page
        if (!skipAutoRedirect) {
          // Mark ref BEFORE setAuthToken so the useEffect below sees it
          // when the Zustand update triggers its re-run.
          justNavigatedFromProcessAuth.current = true;
          navigate(sessionExpiredRedirectionLink, { replace: true });
        }
        localStorage.removeItem('sessionExpiredRedirectTo');
        // Update store AFTER cookies + navigate so any consumers of
        // authToken see the post-navigation state. The useEffect at the
        // bottom of this hook will be suppressed by the ref above.
        setAuthToken(token);
        return { success: true, currentOrganization, currentWorkspace };
      }

      // Navigate — skipped when caller wants to chain its own navigation.
      // Navigate DIRECTLY to redirectPath (not `from = '/'`) so we don't
      // bounce through App.tsx's cookie-based redirect — that's where the
      // stale-cookie flicker used to happen.
      if (!skipAutoRedirect) {
        justNavigatedFromProcessAuth.current = true;
        navigate(redirectPath, { replace: true });
      }
      // Token in Zustand last — see comment near the top of this function.
      setAuthToken(token);
      return { success: true, currentOrganization, currentWorkspace };
    } catch (err) {
      setError('Failed to process authentication. Please try again.');
      console.error('Authentication processing error:', err);
      return { success: false };
    }
  };

  // Handle 2FA required response
  const handleTwoFactorRequired = (userId: string) => {
    setRequireTwoFactor(true);
    return userId;
  };

  // Auto-redirect on token presence — covers the "user lands on /login
  // with a valid cookie" case (e.g. typing the URL directly while already
  // authenticated). Skipped for callers that opt out (e.g. the
  // accept-invitation page, which must keep the user on the page even
  // when a stale token is present in local cookies).
  //
  // TELV1-296 follow-up — gated on the INITIAL token presence to avoid
  // racing `processAuthSuccess`. The previous implementation fired this
  // useEffect whenever `authToken` transitioned from null → truthy,
  // which included the `setAuthToken(token)` call inside
  // `processAuthSuccess`. That meant TWO navigations happened during
  // login (one here, one at the end of `processAuthSuccess`), causing
  // Login to unmount mid-flight, AuthWrapper to render the dashboard
  // shell before user/org data was ready, the dashboard to error out
  // and bounce back to /login, and Login to remount with an empty form
  // — the "flicker" QA reported on staging. Tying the redirect to the
  // FIRST-MOUNT token snapshot decouples it from the in-flight login.
  const initialAuthTokenRef = useRef(authToken);
  useEffect(() => {
    if (!skipAutoRedirect && initialAuthTokenRef.current) {
      navigate(from, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, from, skipAutoRedirect]);

  return {
    authToken,
    setAuthToken,
    setLoginCookies,
    processAuthSuccess,
    handleTwoFactorRequired,
    redirectTo,
    from,
    navigate,
    error,
    setError,
  };
}

export default useAuthentication;
